import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  getBox9Employees,
  positionEmployee,
  getGridConfig,
  updateGridConfig,
  calculatePositions,
  type Box9Employee,
  type Box9GridConfig,
} from '../../lib/box9grid';
import { listUsers, type UserDto } from '../../lib/users';
import {
  Sparkles,
  Settings2,
  UserPlus,
  Info,
  X,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/wawasan/9box')({
  component: NineBoxPage,
});

function NineBoxPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <NineBoxContent />
    </ProtectedRoute>
  );
}

// rows: potential high→low, cols: performance low→high
const GRID_LAYOUT: string[][] = [
  ['future-star', 'high-potential', 'star'],
  ['question-mark', 'average', 'high-performer'],
  ['low-performer', 'underperformer', 'solid'],
];

const QUADRANT_META: Record<
  string,
  { label: string; bg: string; border: string; chip: string; dot: string }
> = {
  star: {
    label: 'Star',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-400',
  },
  'high-performer': {
    label: 'High Performer',
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    chip: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-400',
  },
  solid: {
    label: 'Solid',
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-400',
  },
  'future-star': {
    label: 'Future Star',
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    chip: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-400',
  },
  'high-potential': {
    label: 'High Potential',
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    chip: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    dot: 'bg-indigo-400',
  },
  average: {
    label: 'Average',
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    chip: 'bg-gray-100 text-gray-700 border-gray-200',
    dot: 'bg-gray-400',
  },
  'question-mark': {
    label: 'Question Mark',
    bg: 'bg-orange-50',
    border: 'border-orange-300',
    chip: 'bg-orange-100 text-orange-800 border-orange-200',
    dot: 'bg-orange-400',
  },
  underperformer: {
    label: 'Underperformer',
    bg: 'bg-red-50',
    border: 'border-red-300',
    chip: 'bg-red-100 text-red-700 border-red-200',
    dot: 'bg-red-400',
  },
  'low-performer': {
    label: 'Low Performer',
    bg: 'bg-rose-50',
    border: 'border-rose-300',
    chip: 'bg-rose-100 text-rose-700 border-rose-200',
    dot: 'bg-rose-400',
  },
};

function NineBoxContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:wawasan');
  const isManager = user?.role === 'manager';

  const [employees, setEmployees] = useState<Box9Employee[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  // Division filter: managers default to their own division
  const [showMyDivision, setShowMyDivision] = useState(isManager);
  const [config, setConfig] = useState<Box9GridConfig>({
    performanceThresholds: { low: 33, medium: 66 },
    potentialThresholds: { low: 33, medium: 66 },
  });
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState<string | null>(null);

  // Position modal
  const [posModal, setPosModal] = useState<{
    emp: UserDto | null;
    performance: number;
    potential: number;
  }>({ emp: null, performance: 50, potential: 50 });
  const [posLoading, setPosLoading] = useState(false);

  // Config modal
  const [showConfig, setShowConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState(config);
  const [configSaving, setConfigSaving] = useState(false);

  // Tooltip state for AI reasoning
  const [tooltip, setTooltip] = useState<{ emp: Box9Employee; name: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getBox9Employees(companyId),
      listUsers(companyId, { isActive: true }),
      getGridConfig(companyId),
    ]).then(([empRes, usersRes, cfgRes]) => {
      if (cancelled) return;
      setLoading(false);
      if (empRes.success && empRes.data) setEmployees(empRes.data.employees);
      if (usersRes.success && usersRes.data) setUsers(usersRes.data.users);
      if (cfgRes.success && cfgRes.data) {
        setConfig(cfgRes.data.config);
        setConfigDraft(cfgRes.data.config);
      }
    });
    return () => { cancelled = true; };
  }, [companyId]);

  async function handleCalculate() {
    setCalculating(true);
    setCalcResult(null);
    const res = await calculatePositions(companyId);
    setCalculating(false);
    if (res.success && res.data) {
      setCalcResult(
        `AI positioned ${res.data.positioned} employees using metric and psychometric data.`,
      );
      const empRes = await getBox9Employees(companyId);
      if (empRes.success && empRes.data) setEmployees(empRes.data.employees);
    } else {
      setCalcResult(
        'Auto-calculation complete — no metric or psychometric data found, nothing to position.',
      );
    }
  }

  async function handlePosition() {
    if (!posModal.emp) return;
    setPosLoading(true);
    const res = await positionEmployee(
      posModal.emp.id,
      companyId,
      posModal.performance,
      posModal.potential,
    );
    setPosLoading(false);
    if (res.success && res.data) {
      setEmployees((prev) => {
        const existing = prev.find((e) => e.employeeId === posModal.emp!.id);
        if (existing) {
          return prev.map((e) =>
            e.employeeId === posModal.emp!.id ? res.data!.position : e,
          );
        }
        return [...prev, res.data!.position];
      });
    }
    setPosModal({ emp: null, performance: 50, potential: 50 });
  }

  async function handleSaveConfig() {
    setConfigSaving(true);
    const res = await updateGridConfig(companyId, configDraft);
    setConfigSaving(false);
    if (res.success && res.data) {
      setConfig(res.data.config);
      setShowConfig(false);
    }
  }

  // Users visible in this view (filtered by division for managers)
  const visibleUsers = useMemo(() => {
    if (!showMyDivision || !user?.divisionId) return users;
    return users.filter((u) => u.divisionId === user.divisionId);
  }, [users, showMyDivision, user?.divisionId]);

  // Group employees by quadrant — useMemo so it doesn't re-run on every render
  const byQuadrant = useMemo(() => {
    const visibleIds = new Set(visibleUsers.map((u) => u.id));
    const map: Record<string, Box9Employee[]> = {};
    GRID_LAYOUT.flat().forEach((q) => { map[q] = []; });
    employees.forEach((e) => {
      if (map[e.quadrant] && visibleIds.has(e.employeeId)) map[e.quadrant].push(e);
    });
    return map;
  }, [employees, visibleUsers]);

  const getUserName = (employeeId: string) =>
    users.find((u) => u.id === employeeId)?.name ?? employeeId.slice(0, 8);

  const unpositioned = visibleUsers.filter((u) => !employees.find((e) => e.employeeId === u.id));

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">9-Box Grid</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Performance &times; Potential talent positioning matrix
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isManager && user?.divisionId && (
            <div className="flex items-center rounded-lg border bg-muted p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setShowMyDivision(true)}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  showMyDivision
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                My Division
              </button>
              <button
                type="button"
                onClick={() => setShowMyDivision(false)}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  !showMyDivision
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                All
              </button>
            </div>
          )}
          {canManage && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowConfig(true); setConfigDraft(config); }}
              >
                <Settings2 className="w-4 h-4 mr-1.5" />
                Configure
              </Button>
              <Button
                size="sm"
                onClick={handleCalculate}
                disabled={calculating}
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                {calculating ? 'Calculating…' : 'Auto-Calculate'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Calc result banner */}
      {calcResult && (
        <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          <span>{calcResult}</span>
          <button
            type="button"
            onClick={() => setCalcResult(null)}
            className="text-emerald-500 hover:text-emerald-700 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="flex gap-3">
        {/* Y-axis label */}
        <div className="flex items-center justify-center w-5 shrink-0">
          <span
            className="text-xs text-muted-foreground font-medium whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Potential →
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-3 gap-2">
              {Array(9).fill(0).map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {GRID_LAYOUT.map((row) =>
                  row.map((quadrant) => {
                    const meta = QUADRANT_META[quadrant];
                    const emps = byQuadrant[quadrant] ?? [];
                    return (
                      <div
                        key={quadrant}
                        className={cn(
                          'rounded-xl border-2 p-3 min-h-28 flex flex-col',
                          meta.bg,
                          meta.border,
                        )}
                      >
                        <p className={cn('text-xs font-semibold mb-2 flex items-center gap-1.5')}>
                          <span className={cn('w-2 h-2 rounded-full shrink-0', meta.dot)} />
                          {meta.label}
                          <span className="ml-auto font-normal text-muted-foreground">
                            {emps.length}
                          </span>
                        </p>
                        <div className="flex flex-wrap gap-1 flex-1">
                          {emps.map((e) => {
                            const empUser = users.find((u) => u.id === e.employeeId) ?? null;
                            return (
                              <button
                                key={e.id}
                                type="button"
                                onClick={() => {
                                  if (e.reasoning) {
                                    setTooltip({ emp: e, name: getUserName(e.employeeId) });
                                  } else if (canManage && empUser) {
                                    // Open reposition modal pre-filled with current scores
                                    const perfScore = Math.round(((e.performance / 5) * 100));
                                    const potScore = Math.round(((e.potential / 5) * 100));
                                    setPosModal({ emp: empUser, performance: perfScore, potential: potScore });
                                  }
                                }}
                                title={
                                  e.needsAiReview
                                    ? `${getUserName(e.employeeId)} — Perf: ${e.performance}/5, Pot: ${e.potential}/5 · Auto-updated from metrics — run Auto-Calculate for AI reasoning`
                                    : `${getUserName(e.employeeId)} — Perf: ${e.performance}/5, Pot: ${e.potential}/5${canManage ? ' · Click to reposition' : ''}`
                                }
                                className={cn(
                                  'relative text-xs px-2 py-0.5 rounded-full border font-medium transition-opacity hover:opacity-80',
                                  meta.chip,
                                  (canManage || e.reasoning) ? 'cursor-pointer' : 'cursor-default',
                                )}
                              >
                                {getUserName(e.employeeId).split(' ')[0]}
                                {e.reasoning && (
                                  <span className="ml-0.5 opacity-60">
                                    <Info className="w-2.5 h-2.5 inline-block" />
                                  </span>
                                )}
                                {e.needsAiReview && (
                                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 border border-white animate-pulse" />
                                )}
                              </button>
                            );
                          })}
                          {emps.length === 0 && (
                            <span className="text-xs text-muted-foreground opacity-50 self-start">
                              —
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }),
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Performance →
              </p>
            </>
          )}
        </div>
      </div>

      {/* Manual positioning */}
      {canManage && !loading && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary" />
              Manual Positioning
              <Badge variant="secondary" className="ml-auto font-normal">
                {unpositioned.length} unpositioned
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {unpositioned.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                All employees have been positioned in the grid.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {unpositioned.slice(0, 12).map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setPosModal({ emp: u, performance: 50, potential: 50 })}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-lg text-foreground hover:bg-accent hover:border-primary/40 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    {u.name}
                  </button>
                ))}
                {unpositioned.length > 12 && (
                  <span className="text-xs text-muted-foreground self-center">
                    + {unpositioned.length - 12} more
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Reasoning tooltip dialog */}
      <Dialog open={!!tooltip} onOpenChange={() => setTooltip(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">
              {tooltip?.name}
            </DialogTitle>
          </DialogHeader>
          {tooltip && (
            <div className="space-y-3">
              <div className="flex gap-4">
                <div className="text-center flex-1 bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Performance</p>
                  <p className="text-xl font-bold text-foreground">{tooltip.emp.performance}<span className="text-xs font-normal text-muted-foreground">/5</span></p>
                </div>
                <div className="text-center flex-1 bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Potential</p>
                  <p className="text-xl font-bold text-foreground">{tooltip.emp.potential}<span className="text-xs font-normal text-muted-foreground">/5</span></p>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI Reasoning
                </p>
                <p className="text-sm text-foreground leading-relaxed">{tooltip.emp.reasoning}</p>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Link
              to={'/reports' as any}
              search={{ employee: tooltip?.name } as any}
              onClick={() => setTooltip(null)}
              className="flex-1 text-center text-xs px-3 py-1.5 border border-stone-200 text-stone-700 rounded-md hover:bg-stone-50 transition-colors font-medium"
            >
              View Report →
            </Link>
            <Link
              to={'/idp' as any}
              onClick={() => setTooltip(null)}
              className="flex-1 text-center text-xs px-3 py-1.5 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 transition-colors font-medium"
            >
              View IDP →
            </Link>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTooltip(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Position modal */}
      <Dialog
        open={!!posModal.emp}
        onOpenChange={() => setPosModal({ emp: null, performance: 50, potential: 50 })}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{posModal.emp?.name}</DialogTitle>
          </DialogHeader>
          {/* Quick nav links */}
          <div className="flex gap-2 -mt-1">
            <Link
              to={'/reports' as any}
              search={{ employee: posModal.emp?.name } as any}
              onClick={() => setPosModal({ emp: null, performance: 50, potential: 50 })}
              className="flex-1 text-center text-xs px-3 py-1.5 border border-stone-200 text-stone-700 rounded-md hover:bg-stone-50 transition-colors font-medium"
            >
              View Report →
            </Link>
            <Link
              to={'/idp' as any}
              onClick={() => setPosModal({ emp: null, performance: 50, potential: 50 })}
              className="flex-1 text-center text-xs px-3 py-1.5 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 transition-colors font-medium"
            >
              View IDP →
            </Link>
          </div>
          <p className="text-xs font-medium text-muted-foreground -mb-1">Reposition</p>
          <div className="space-y-5">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Performance</label>
                <span className="text-sm font-semibold text-primary">{posModal.performance}</span>
              </div>
              <Slider
                min={0}
                max={100}
                step={1}
                value={[posModal.performance]}
                onValueChange={([v]) => setPosModal((f) => ({ ...f, performance: v }))}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Potential</label>
                <span className="text-sm font-semibold text-primary">{posModal.potential}</span>
              </div>
              <Slider
                min={0}
                max={100}
                step={1}
                value={[posModal.potential]}
                onValueChange={([v]) => setPosModal((f) => ({ ...f, potential: v }))}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPosModal({ emp: null, performance: 50, potential: 50 })}
            >
              Cancel
            </Button>
            <Button onClick={handlePosition} disabled={posLoading}>
              {posLoading ? 'Saving…' : 'Position'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Grid Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground">
              Thresholds define the Low / Medium / High band boundaries (1–5 scale).
              These are saved for reference; the AI auto-calculation uses its own scoring model.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Performance — Low threshold</label>
                <span className="text-sm font-semibold text-primary">{configDraft.performanceThresholds.low}</span>
              </div>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={[configDraft.performanceThresholds.low]}
                onValueChange={([v]) =>
                  setConfigDraft((f) => ({
                    ...f,
                    performanceThresholds: { ...f.performanceThresholds, low: Math.round(v * 10) / 10 },
                  }))
                }
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Performance — Medium threshold</label>
                <span className="text-sm font-semibold text-primary">{configDraft.performanceThresholds.medium}</span>
              </div>
              <Slider
                min={3}
                max={5}
                step={0.1}
                value={[configDraft.performanceThresholds.medium]}
                onValueChange={([v]) =>
                  setConfigDraft((f) => ({
                    ...f,
                    performanceThresholds: { ...f.performanceThresholds, medium: Math.round(v * 10) / 10 },
                  }))
                }
              />
            </div>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Potential — Low threshold</label>
                <span className="text-sm font-semibold text-primary">{configDraft.potentialThresholds.low}</span>
              </div>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={[configDraft.potentialThresholds.low]}
                onValueChange={([v]) =>
                  setConfigDraft((f) => ({
                    ...f,
                    potentialThresholds: { ...f.potentialThresholds, low: Math.round(v * 10) / 10 },
                  }))
                }
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Potential — Medium threshold</label>
                <span className="text-sm font-semibold text-primary">{configDraft.potentialThresholds.medium}</span>
              </div>
              <Slider
                min={3}
                max={5}
                step={0.1}
                value={[configDraft.potentialThresholds.medium]}
                onValueChange={([v]) =>
                  setConfigDraft((f) => ({
                    ...f,
                    potentialThresholds: { ...f.potentialThresholds, medium: Math.round(v * 10) / 10 },
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={configSaving}>
              {configSaving ? 'Saving…' : 'Save Config'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
